import { useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Lightbulb, Save, Trash2 } from "lucide-react";
import { Button, Card, Input, Textarea } from "@samklang/ui";
import { useKartlaggningStore, type KartlaggningAction } from "@/stores/kartlaggning-store";
import { actionStatusLabel, buildSamladBild, formatDate } from "./lib";

interface Props {
  readonly kartlaggningId: string;
}

const STATUSES: readonly KartlaggningAction["status"][] = ["planned", "in_progress", "done", "cancelled"];

const emptyForm = { description: "", purpose: "", responsible: "", startDate: "", followUpDate: "", followUpMethod: "", linkedNeed: "" };

export function KartlaggningInsatsScreen({ kartlaggningId }: Props) {
  const navigate = useNavigate();
  const kartlaggning = useKartlaggningStore((store) => store.getKartlaggning(kartlaggningId));
  const addAction = useKartlaggningStore((store) => store.addAction);
  const updateAction = useKartlaggningStore((store) => store.updateAction);
  const updateActionStatus = useKartlaggningStore((store) => store.updateActionStatus);
  const removeAction = useKartlaggningStore((store) => store.removeAction);

  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState<string | undefined>(undefined);
  const [draftStatus, setDraftStatus] = useState<KartlaggningAction["status"]>("planned");

  const needs = useMemo(() => (kartlaggning ? buildSamladBild(kartlaggning) : undefined), [kartlaggning]);

  if (!kartlaggning) {
    return (
      <section className="mx-auto max-w-2xl px-5 py-10">
        <Card><h1 className="font-display text-2xl font-semibold">Kartläggningen finns inte.</h1></Card>
      </section>
    );
  }

  const seedFromNeed = (question: string, answer: string) => {
    setEditingId(undefined);
    setDraftStatus("planned");
    setForm({ ...emptyForm, description: `Uppmärksammat behov: ${question} — ${answer}`, linkedNeed: question });
  };

  const save = () => {
    if (!form.description.trim()) return;
    const payload = { ...form, description: form.description.trim(), purpose: form.purpose.trim(), responsible: form.responsible.trim(), followUpMethod: form.followUpMethod.trim(), linkedNeed: form.linkedNeed.trim() };
    if (editingId) {
      updateAction(kartlaggningId, editingId, payload);
    } else {
      addAction(kartlaggningId, { ...payload, status: draftStatus });
    }
    setForm(emptyForm);
    setEditingId(undefined);
    setDraftStatus("planned");
  };

  const startEdit = (action: KartlaggningAction) => {
    setEditingId(action.id);
    setForm({ description: action.description, purpose: action.purpose, responsible: action.responsible, startDate: action.startDate, followUpDate: action.followUpDate, followUpMethod: action.followUpMethod, linkedNeed: action.linkedNeed });
    setDraftStatus(action.status);
  };

  return (
    <section className="mx-auto w-full max-w-3xl px-5 py-8 sm:px-8">
      <button onClick={() => void navigate({ to: "/kartlaggningar/$kartlaggningId", params: { kartlaggningId } })} className="flex items-center gap-1 text-sm font-semibold text-primary">
        <ArrowLeft size={16} /> Till kartläggningen
      </button>
      <h1 className="mt-3 font-display text-4xl font-semibold">Insatser</h1>
      <p className="mt-2 text-foreground-muted">Koppla behov från kartläggningen till konkreta insatser och överenskommelser.</p>
      <p className="mt-2 rounded-2xl bg-surface-muted p-3 text-sm text-foreground-muted">
        Exempel: "Eleven ska erbjudas möjlighet att arbeta i mindre sammanhang under matematik."
      </p>

      {needs && (needs.needs.length > 0 || needs.difficulties.length > 0) ? (
        <Card className="mt-6">
          <h2 className="flex items-center gap-2 font-display text-lg font-semibold"><Lightbulb size={19} className="text-primary" /> Föreslagna utifrån kartläggningen</h2>
          <p className="mt-1 text-sm text-foreground-muted">Klicka för att skapa en insats från ett behov.</p>
          <ul className="mt-4 space-y-2">
            {[...needs.needs, ...needs.difficulties].map((entry, index) => (
              <li key={`${entry.item.questionId}-${index}`}>
                <button onClick={() => seedFromNeed(entry.item.question, entry.item.answer)} className="w-full rounded-2xl border border-border bg-surface-raised p-3 text-left text-sm hover:border-primary/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                  <span className="block text-foreground-subtle">[{entry.label}]</span>
                  <span className="block">{entry.item.question}: {entry.item.answer}</span>
                </button>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      <Card className="mt-6">
        <h2 className="font-display text-lg font-semibold">{editingId ? "Redigera insats" : "Ny insats"}</h2>
        <div className="mt-4 grid gap-3">
          <label className="grid gap-1 font-semibold">
            Vad (insatsen)
            <Textarea value={form.description} onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))} maxLength={1200} placeholder="Beskriv insatsen" />
          </label>
          <label className="grid gap-1 font-semibold">
            Syfte
            <Input value={form.purpose} onChange={(event) => setForm((current) => ({ ...current, purpose: event.target.value }))} maxLength={400} placeholder="Vad ska insatsen uppnå?" />
          </label>
          <div className="grid gap-3 min-[480px]:grid-cols-2">
            <label className="grid gap-1 font-semibold">
              Ansvarig
              <Input value={form.responsible} onChange={(event) => setForm((current) => ({ ...current, responsible: event.target.value }))} maxLength={120} placeholder="Vem ansvarar?" />
            </label>
            <label className="grid gap-1 font-semibold">
              Kopplat behov
              <Input value={form.linkedNeed} onChange={(event) => setForm((current) => ({ ...current, linkedNeed: event.target.value }))} maxLength={200} placeholder="Vilket behov?" />
            </label>
            <label className="grid gap-1 font-semibold">
              Startdatum
              <Input type="date" value={form.startDate} onChange={(event) => setForm((current) => ({ ...current, startDate: event.target.value }))} />
            </label>
            <label className="grid gap-1 font-semibold">
              Uppföljningsdatum
              <Input type="date" value={form.followUpDate} onChange={(event) => setForm((current) => ({ ...current, followUpDate: event.target.value }))} />
            </label>
          </div>
          <label className="grid gap-1 font-semibold">
            Hur följs upp
            <Input value={form.followUpMethod} onChange={(event) => setForm((current) => ({ ...current, followUpMethod: event.target.value }))} maxLength={300} placeholder="t.ex. Fråga eleven i mentorssamtal" />
          </label>
          <fieldset>
            <legend className="mb-2 text-sm font-semibold">Status</legend>
            <div className="flex flex-wrap gap-2">
              {STATUSES.map((status) => (
                <button key={status} type="button" onClick={() => setDraftStatus(status)} aria-pressed={draftStatus === status} className={`min-h-11 rounded-full border px-4 text-sm font-semibold ${draftStatus === status ? "border-primary bg-primary-soft text-primary" : "border-border text-foreground-muted"}`}>
                  {actionStatusLabel(status)}
                </button>
              ))}
            </div>
          </fieldset>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button onClick={save} disabled={!form.description.trim()}>
            <Save size={18} /> {editingId ? "Spara ändringar" : "Lägg till insats"}
          </Button>
          {editingId ? <Button variant="ghost" onClick={() => { setEditingId(undefined); setForm(emptyForm); setDraftStatus("planned"); }}>Avbryt</Button> : null}
        </div>
      </Card>

      <div className="mt-6">
        <h2 className="font-display text-xl font-semibold">Registrerade insatser</h2>
        {kartlaggning.actions.length === 0 ? (
          <p className="mt-3 text-foreground-muted">Inga insatser registrerade ännu.</p>
        ) : (
          <div className="mt-3 grid gap-3">
            {kartlaggning.actions.map((action) => (
              <Card key={action.id} className="px-5 py-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-semibold">{action.description}</p>
                    {action.purpose ? <p className="mt-1 text-sm text-foreground-muted"><strong>Syfte:</strong> {action.purpose}</p> : null}
                    <p className="mt-1 text-sm text-foreground-muted">
                      Ansvarig: {action.responsible || "—"} · Start: {formatDate(action.startDate)} · Uppföljning: {formatDate(action.followUpDate)} · Följs upp: {action.followUpMethod || "—"}
                    </p>
                    {action.linkedNeed ? <p className="mt-1 text-sm text-foreground-subtle">Kopplat behov: {action.linkedNeed}</p> : null}
                  </div>
                  <button onClick={() => removeAction(kartlaggningId, action.id)} aria-label={`Radera insats`} className="mt-1 rounded-full p-2 text-foreground-muted hover:text-danger focus-visible:outline-none focus-visible:ring-2">
                    <Trash2 size={18} />
                  </button>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-surface-muted px-3 py-1 text-sm font-medium text-foreground-muted">{actionStatusLabel(action.status)}</span>
                  <div className="flex flex-wrap gap-1">
                    {STATUSES.filter((status) => status !== action.status).map((status) => (
                      <button key={status} onClick={() => updateActionStatus(kartlaggningId, action.id, status)} className="min-h-9 rounded-full border border-border px-3 text-xs font-semibold text-foreground-muted">
                        {actionStatusLabel(status)}
                      </button>
                    ))}
                  </div>
                  <button onClick={() => startEdit(action)} className="ml-auto min-h-9 rounded-full border border-border px-3 text-xs font-semibold text-primary">
                    Redigera
                  </button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
